import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { ArrowLeft, Calendar, Clock, MapPin, Users, Copy, CheckCircle2, Building2, Save, FileBarChart, Download, Bell } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CorporateEventDetail() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [volunteers, setVolunteers] = useState([]);
  const [impact, setImpact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [impactForm, setImpactForm] = useState({
    bookshelves_built: 0,
    families_served: 0,
    total_hours: 0
  });

  useEffect(() => {
    fetchData();
  }, [eventId]);

  const fetchData = async () => {
    try {
      const [eventRes, volunteersRes, impactRes] = await Promise.all([
        fetch(`${API}/corporate/events/${eventId}`, { credentials: 'include' }),
        fetch(`${API}/corporate/events/${eventId}/volunteers`, { credentials: 'include' }),
        fetch(`${API}/corporate/events/${eventId}/impact`, { credentials: 'include' })
      ]);

      if (eventRes.ok) {
        const eventData = await eventRes.json();
        setEvent(eventData);
        setImpactForm({
          bookshelves_built: eventData.bookshelves_built || 0,
          families_served: eventData.families_served || 0,
          total_hours: eventData.total_hours || 0
        });
      }
      if (volunteersRes.ok) setVolunteers(await volunteersRes.json());
      if (impactRes.ok) setImpact(await impactRes.json());
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load event data');
    } finally {
      setLoading(false);
    }
  };

  const copyRegistrationLink = () => {
    const url = `${window.location.origin}/corporate/register/${event.registration_token}`;
    navigator.clipboard.writeText(url);
    toast.success('Registration link copied!');
  };

  const checkinVolunteer = async (volunteerId) => {
    try {
      const response = await fetch(`${API}/corporate/volunteers/${volunteerId}/checkin`, {
        method: 'PUT',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Volunteer checked in');
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to check in volunteer');
    }
  };

  const saveImpact = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API}/corporate/events/${eventId}/impact`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(impactForm)
      });

      if (response.ok) {
        toast.success('Impact metrics saved');
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to save impact metrics');
    } finally {
      setSaving(false);
    }
  };

  const markCompleted = async () => {
    try {
      const response = await fetch(`${API}/corporate/events/${eventId}/impact`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ ...impactForm, status: 'Completed' })
      });

      if (response.ok) {
        toast.success('Event marked as completed');
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to update event status');
    }
  };

  const generateImpactReport = async () => {
    try {
      const response = await fetch(`${API}/admin/corporate/events/${eventId}/generate-report`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Impact report sent to ${data.sent_to}!`);
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to generate report');
      }
    } catch (error) {
      toast.error('Failed to generate impact report');
    }
  };

  const downloadCalendar = () => {
    window.open(`${API}/corporate/events/${eventId}/calendar`, '_blank');
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-[#5cc8e8] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </AdminLayout>
    );
  }

  if (!event) {
    return (
      <AdminLayout>
        <div className="text-center py-12">
          <p className="text-slate-500">Event not found</p>
          <Button onClick={() => navigate('/admin/corporate')} className="mt-4">
            Back to Corporate Dashboard
          </Button>
        </div>
      </AdminLayout>
    );
  }

  const checkedInCount = volunteers.filter(v => v.checked_in).length;

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="corporate-event-detail">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/admin/corporate')} data-testid="back-btn">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">
                <Building2 className="w-4 h-4" />
                {event.company_name}
              </div>
              <h1 className="text-2xl font-bold text-[#1a3a5c]" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {event.title}
              </h1>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={downloadCalendar} data-testid="download-calendar-btn" title="Download calendar file">
              <Download className="w-4 h-4 mr-2" />
              Calendar
            </Button>
            <Button variant="outline" onClick={copyRegistrationLink} data-testid="copy-link-btn">
              <Copy className="w-4 h-4 mr-2" />
              Copy Link
            </Button>
            {event.status === 'Completed' && !event.impact_report_sent && (
              <Button onClick={generateImpactReport} className="bg-purple-600 hover:bg-purple-700" data-testid="generate-report-btn">
                <FileBarChart className="w-4 h-4 mr-2" />
                Send Impact Report
              </Button>
            )}
            {event.impact_report_sent && (
              <Badge className="bg-purple-100 text-purple-800 flex items-center">
                <FileBarChart className="w-3 h-3 mr-1" />
                Report Sent
              </Badge>
            )}
            {event.status !== 'Completed' && (
              <Button onClick={markCompleted} className="bg-emerald-600 hover:bg-emerald-700">
                <CheckCircle2 className="w-4 h-4 mr-2" />
                Mark Completed
              </Button>
            )}
          </div>
        </div>

        {/* Event Info */}
        <div className="grid md:grid-cols-3 gap-4">
          <Card className="border-0 shadow-md">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Calendar className="w-5 h-5 text-[#5cc8e8]" />
                <div>
                  <p className="text-xs text-slate-500">Date</p>
                  <p className="font-medium text-[#1a3a5c]">
                    {new Date(event.date).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-md">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-[#5cc8e8]" />
                <div>
                  <p className="text-xs text-slate-500">Time</p>
                  <p className="font-medium text-[#1a3a5c]">{event.start_time} - {event.end_time}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-md">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <MapPin className="w-5 h-5 text-[#5cc8e8]" />
                <div>
                  <p className="text-xs text-slate-500">Location</p>
                  <p className="font-medium text-[#1a3a5c]">{event.location}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Stats Row */}
        <div className="grid sm:grid-cols-4 gap-4">
          <Card className="border-0 shadow-md">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-[#1a3a5c]">{volunteers.length}</p>
              <p className="text-sm text-slate-500">Registered</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-md">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-emerald-600">{checkedInCount}</p>
              <p className="text-sm text-slate-500">Checked In</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-md">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-[#F4D03F]">{event.bookshelves_goal}</p>
              <p className="text-sm text-slate-500">Bookshelf Goal</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-md">
            <CardContent className="p-4 text-center">
              <Badge className={event.status === 'Completed' ? 'bg-emerald-100 text-emerald-800' : 'bg-blue-100 text-blue-800'}>
                {event.status}
              </Badge>
            </CardContent>
          </Card>
        </div>

        {/* Two Column Layout */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Volunteers List */}
          <Card className="border-0 shadow-md">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                Registered Volunteers ({volunteers.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {volunteers.length > 0 ? (
                <div className="max-h-96 overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead>Name</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {volunteers.map((volunteer) => (
                        <TableRow key={volunteer.volunteer_id}>
                          <TableCell className="font-medium">{volunteer.name}</TableCell>
                          <TableCell className="text-sm text-slate-500">{volunteer.email}</TableCell>
                          <TableCell>
                            {volunteer.checked_in ? (
                              <Badge className="bg-emerald-100 text-emerald-800">Checked In</Badge>
                            ) : (
                              <Badge className="bg-slate-100 text-slate-600">Registered</Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            {!volunteer.checked_in && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => checkinVolunteer(volunteer.volunteer_id)}
                              >
                                Check In
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="p-8 text-center text-slate-500">
                  <Users className="w-10 h-10 mx-auto mb-3 text-slate-300" />
                  <p>No volunteers registered yet</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Impact Tracking */}
          <Card className="border-0 shadow-md">
            <CardHeader>
              <CardTitle>Impact Tracking</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="bookshelves_built">Bookshelves Built</Label>
                <Input
                  id="bookshelves_built"
                  type="number"
                  value={impactForm.bookshelves_built}
                  onChange={(e) => setImpactForm(prev => ({ ...prev, bookshelves_built: parseInt(e.target.value) || 0 }))}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="families_served">Families Served</Label>
                <Input
                  id="families_served"
                  type="number"
                  value={impactForm.families_served}
                  onChange={(e) => setImpactForm(prev => ({ ...prev, families_served: parseInt(e.target.value) || 0 }))}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="total_hours">Total Volunteer Hours</Label>
                <Input
                  id="total_hours"
                  type="number"
                  step="0.5"
                  value={impactForm.total_hours}
                  onChange={(e) => setImpactForm(prev => ({ ...prev, total_hours: parseFloat(e.target.value) || 0 }))}
                />
              </div>

              <Button onClick={saveImpact} disabled={saving} className="w-full bg-[#1a3a5c] hover:bg-[#1a3a5c]/90">
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Saving...' : 'Save Impact Metrics'}
              </Button>

              {/* Impact Summary */}
              <div className="mt-6 p-4 bg-[#1a3a5c] rounded-xl text-white">
                <h4 className="font-semibold mb-3">Impact Report</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-white/70">Company</p>
                    <p className="font-medium">{event.company_name}</p>
                  </div>
                  <div>
                    <p className="text-white/70">Volunteers</p>
                    <p className="font-medium">{checkedInCount} checked in</p>
                  </div>
                  <div>
                    <p className="text-white/70">Hours</p>
                    <p className="font-medium">{impactForm.total_hours}</p>
                  </div>
                  <div>
                    <p className="text-white/70">Bookshelves</p>
                    <p className="font-medium">{impactForm.bookshelves_built}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </AdminLayout>
  );
}
